// frontend/src/screens/AdminDashboardScreen.js
import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAdminData } from "../actions/adminAction";
import { Link, useNavigate } from "react-router-dom";
import "./AdminDashboardScreen.css";
import { deleteDeveloper } from "../actions/developerActions";
import { deleteProject } from "../actions/projectActions";
import { DEVELOPER_DELETE_RESET } from "../constants/developerConstants";

const AdminDashboardScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Grab the developerDelete slice
  const developerDelete = useSelector((state) => state.developerDelete);
  const {
    loading: deleteLoading,
    developerDeleteSuccess,
    error: deleteError,
  } = developerDelete;

  const adminData = useSelector((state) => state.adminData);
  const { loading, error, developersProjects } = adminData;

  // Sort state (asc or desc)
  const [sortOrder, setSortOrder] = useState("asc");

  // Sort developers by name
  const sortedDevelopers = useMemo(() => {
    if (!developersProjects) return [];
    const sorted = [...developersProjects].sort((a, b) =>
      a.developerName.localeCompare(b.developerName)
    );
    return sortOrder === "asc" ? sorted : sorted.reverse();
  }, [developersProjects, sortOrder]);

  // Toggle sort order
  const handleSortByName = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  // Fetch admin data on component mount
  useEffect(() => {
    dispatch(fetchAdminData());
  }, [dispatch]);

  // Navigate to add new developer screen
  const handleAddNewDeveloper = () => {
    navigate("/add-developer");
  };

  // Navigate to add project
  const handleAddProject = (developerId, developerName) => {
    navigate(`/add-project/${developerId}/${developerName}`);
  };

  // Placeholder handlers for editing/deleting a developer
  const handleEditDeveloper = (developerId) => {
    console.log("Edit developer with ID:", developerId);
    navigate(`/edit-developer/${developerId}`);
  };

  const handleDeleteDeveloper = (developerId) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this developer and all their projects?"
    );
    if (!confirmed) return;
    dispatch(deleteDeveloper(developerId));
  };

  // When the deletion succeeds, we can do one of these:
  // 1) Refresh admin data to reload the developer list
  // 2) Or navigate to a known route (like /admin-dashboard)
  useEffect(() => {
    if (developerDeleteSuccess) {
      dispatch(fetchAdminData());
      // Then reset so we don't keep developerDeleteSuccess = true forever
      dispatch({ type: DEVELOPER_DELETE_RESET });
    }
  }, [developerDeleteSuccess, dispatch]);

  // Placeholder handlers for editing/deleting a project
  const handleEditProject = (projectId) => {
    navigate(`/edit-project/${projectId}`);
  };

  const handleDeleteProject = (projectId) => {
    if (!window.confirm("Are you sure you want to delete this project?"))
      return;
    dispatch(deleteProject(projectId));
  };

  const projectDelete = useSelector((state) => state.projectDelete);
  const { success: projectDeleteSuccess } = projectDelete;

  useEffect(() => {
    if (projectDeleteSuccess) {
      // either refresh the admin data
      dispatch(fetchAdminData());
      // or navigate("/admin-dashboard")
    }
  }, [projectDeleteSuccess, dispatch]);

  if (loading) return <p>Loading...</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;

  return (
    <div>
      {/* If there's a delete in progress, show a small loader */}
      {deleteLoading && <p>Deleting developer...</p>}
      {deleteError && <p style={{ color: "red" }}>{deleteError}</p>}
      <div className="admin-dashboard-container">
        {/* 1) Header alone (left-aligned) */}
        <h2 className="admin-dashboard-title">Admin Dashboard</h2>

        {/*
        2) Second row with:
           - "Add New Developer" link on the LEFT
           - "Sort by Developer Name" link on the RIGHT
           - Space between them via flexbox
      */}
        <div className="admin-dashboard-actions">
          <span className="action-link" onClick={handleAddNewDeveloper}>
            Add New Developer
          </span>

          <span className="action-link" onClick={handleSortByName}>
            Sort by Developer Name ({sortOrder.toUpperCase()})
          </span>
        </div>

        {/* 3) Render developers */}
        {sortedDevelopers && sortedDevelopers.length > 0 ? (
          sortedDevelopers.map((devObj) => (
            <div key={devObj.developerId} className="developer-section">
              {/* Developer Row: Name on the left, Edit/Delete on the right */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "0.5rem",
                }}
              >
                <h3 style={{ margin: 0 }}>{devObj.developerName}</h3>
                <div style={{ display: "inline-flex", gap: "0.5rem" }}>
                  <button
                    style={{
                      fontSize: "0.8rem",
                      padding: "0.3rem 0.6rem",
                    }}
                    onClick={() => handleEditDeveloper(devObj.developerId)}
                  >
                    Edit
                  </button>
                  <button
                    style={{
                      fontSize: "0.8rem",
                      padding: "0.3rem 0.6rem",
                    }}
                    onClick={() => handleDeleteDeveloper(devObj.developerId)}
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* Projects list under each developer */}
              {devObj.projects.length > 0 ? (
                <ul className="project-list">
                  {devObj.projects.map((proj) => (
                    <li
                      key={proj._id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "0.5rem",
                      }}
                    >
                      {/* Project name (left) */}
                      <Link
                        to={`/posts/${proj._id}`}
                        style={{ marginRight: "auto", textDecoration: "none" }}
                      >
                        {proj.name}
                      </Link>

                      {/* Project edit/delete (right) */}
                      <div style={{ display: "inline-flex", gap: "0.5rem" }}>
                        <button
                          style={{
                            fontSize: "0.8rem",
                            padding: "0.3rem 0.6rem",
                          }}
                          onClick={() => handleEditProject(proj._id)}
                        >
                          Edit
                        </button>
                        <button
                          style={{
                            fontSize: "0.8rem",
                            padding: "0.3rem 0.6rem",
                          }}
                          onClick={() => handleDeleteProject(proj._id)}
                        >
                          Delete
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No projects for this developer.</p>
              )}

              {/* Add Project button below the project list */}
              <button
                className="add-project-button"
                onClick={() =>
                  handleAddProject(devObj.developerId, devObj.developerName)
                }
                style={{ marginTop: "0.5rem" }}
              >
                Add Project
              </button>
            </div>
          ))
        ) : (
          <p>No developers or projects to display.</p>
        )}
      </div>
    </div>
  );
};

export default AdminDashboardScreen;
